import { createApi } from '@reduxjs/toolkit/query/react'
import axiosBaseQuery from 'api/axiosBaseQuery'
import {
  LoginFormData,
  LoginResponse,
  ResendActivationPayload,
  ResendActivationResponse,
  ActivateAccountPayload,
  ActivateAccountResponse,
  MfaSetupInitPayload,
  MfaSetupApiResponse,
  MfaConfirmPayload,
  MfaVerifyPayload
} from 'pages/login/types'
import {
  RegisterPayload,
  RegisterResponse,
  VerifyIdentityPayload,
  VerifyIdentityResponse
} from 'pages/register/types'
import {
  ForgotPasswordPayload,
  ForgotPasswordResponse
} from 'pages/forgot-password/types'
import {
  ResetPasswordPayload,
  ResetPasswordResponse
} from 'pages/reset-password/types'

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: axiosBaseQuery({ baseUrl: process.env.REACT_APP_BASE_URL ?? '' }),
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginFormData>({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        data: credentials,
        headers: {
          'Content-Type': 'application/json'
        },
        includeAuth: false
      })
    }),
    register: builder.mutation<RegisterResponse, RegisterPayload>({
      query: (data) => ({
        url: '/auth/register',
        method: 'POST',
        data,
        headers: {
          'Content-Type': 'application/json'
        },
        includeAuth: false
      })
    }),
    verifyIdentity: builder.mutation<
      VerifyIdentityResponse,
      VerifyIdentityPayload
    >({
      query: (data) => ({
        url: '/gava-connect/verify-identity',
        method: 'POST',
        data,
        headers: {
          'Content-Type': 'application/json'
        },
        includeAuth: false
      })
    }),
    forgotPassword: builder.mutation<
      ForgotPasswordResponse,
      ForgotPasswordPayload
    >({
      query: (data) => ({
        url: '/auth/forgot-password',
        method: 'POST',
        data,
        headers: {
          'Content-Type': 'application/json'
        },
        includeAuth: false
      })
    }),
    resetPassword: builder.mutation<
      ResetPasswordResponse,
      ResetPasswordPayload
    >({
      query: (data) => ({
        url: '/auth/reset-password',
        method: 'POST',
        data,
        headers: {
          'Content-Type': 'application/json'
        },
        includeAuth: false
      })
    }),
    resendActivation: builder.mutation<
      ResendActivationResponse,
      ResendActivationPayload
    >({
      query: (data) => ({
        url: '/auth/resend-activation',
        method: 'POST',
        data,
        headers: {
          'Content-Type': 'application/json'
        },
        includeAuth: false
      })
    }),
    activateAccount: builder.mutation<
      ActivateAccountResponse,
      ActivateAccountPayload
    >({
      query: ({ token }) => ({
        url: `/auth/activate?token=${token}`,
        method: 'GET',
        includeAuth: false
      })
    }),
    changePassword: builder.mutation<any, ChangePasswordPayload>({
      query: (data) => ({
        url: '/auth/change-password',
        method: 'POST',
        data,
        headers: {
          'Content-Type': 'application/json'
        }
      })
    }),
    // ---- MFA: begin enrollment (returns secret + otpauth URI) ----
    mfaSetup: builder.mutation<MfaSetupApiResponse, MfaSetupInitPayload>({
      query: (data) => ({
        url: '/auth/mfa/setup',
        method: 'POST',
        data,
        headers: {
          'Content-Type': 'application/json'
        },
        includeAuth: false
      })
    }),
    // ---- MFA: confirm enrollment with two consecutive codes → logs in ----
    mfaConfirm: builder.mutation<LoginResponse, MfaConfirmPayload>({
      query: (data) => ({
        url: '/auth/mfa/confirm',
        method: 'POST',
        data,
        headers: {
          'Content-Type': 'application/json'
        },
        includeAuth: false
      })
    }),
    // ---- MFA: verify login-time code → logs in ----
    mfaVerify: builder.mutation<LoginResponse, MfaVerifyPayload>({
      query: (data) => ({
        url: '/auth/mfa/verify',
        method: 'POST',
        data,
        headers: {
          'Content-Type': 'application/json'
        },
        includeAuth: false
      })
    })
  })
})

export interface ChangePasswordPayload {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

export const {
  useLoginMutation,
  useRegisterMutation,
  useVerifyIdentityMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useResendActivationMutation,
  useActivateAccountMutation,
  useChangePasswordMutation,
  useMfaSetupMutation,
  useMfaConfirmMutation,
  useMfaVerifyMutation
} = authApi
